import { Action, State, StateContext, Store } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { StateHelperService } from '@core/services/state-helper.service';
import {
  MbbProductAddAction,
  MbbProductRemoveConfirmAction,
  MbbProductResetAction,
  MbbProductSubscriberUpdateAction,
  MbbProductUpdateAction,
  UpdateMbbDeliveryOptionAction,
  SetExistingMbbSubscriptionsAction,
  ApiGetAssignedMbbSubscriptionsAction,
  ApiGetAssignedMbbSubscriptionsErrorAction,
  ApiGetAssignedMbbSubscriptionsSuccessAction,
  SetAssignedMbbSubscriptionAction,
  ApiGetMbbSubscriptionOfferingsAction,
  ApiGetMbbSubscriptionOfferingsSuccessAction,
  ApiGetMbbSubscriptionOfferingsErrorAction,
  SetSelectedMbbStepIndexAction,
  ToggleSelectedMbbSubscriptionAction,
  ResetSelectedMbbSubscriptionAction,
  SetSelectedMbbSubscriptionIdAction,
  SetPaymentTypeOnMBBAction,
  UpdateMbbRouterDetailsAction,
} from './customer-product-mbb.actions';
import { MbbProduct } from '../models/mbb-product.model';
import {
  AddOrUpdateShippingItemAction,
  AddShippingItemAction,
  RemoveShippingItemAction,
} from '@sales/states/logistics/logistics.actions';
import { inject, Injectable } from '@angular/core';
import { ProductClassifications } from '@core/enums/product-classifications';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { Observable } from 'rxjs';
import { FormBuilder, FormArray } from '@angular/forms';
import { Subscription } from '@shared/models/subscription-model';
import { SubscriptionService } from '@sales/services/subscription.service';
import { tap } from 'rxjs/operators';
import { AssignedSubscriptionModel } from '@shared/models/assigned-subscription-offering.model';
import { NotificationService } from '@core/services/notification.service';
import { MbbSubscriptionOffering, MbbRouter } from '../models/mbb-offerings-response.model';
import { ProductStartIndex } from '@shared/models/product.model';
import { PaymentType } from '@shared/enums/payment-type';
import { ShoppingCartChildItem } from '@sales/models/shopping-cart-item.model';
import { UpdateMbbFormValidStatusAction } from '@sales/states/form/form.actions';
import { IconNames } from '@shared/enums/icon-names';
import { MbbDeliveryOptions } from '../models/mbb-delivery-options.model';
import { SimService } from '@sales/services/sim.service';
import { FormStateModel } from '@app/sales/states/form/form.types';
import { getCorrelatedBundle } from '@app/core/utils';

enum Messages {
  AssignedOfferingForSubscriptionsError = 'Feil ved henting av eksisterende MBB abonnement.',
  GetMbbSubscriptionOfferingsError = 'Feil ved henting av MBB abonnementer',
}

export interface CustomerProductsMbbStateModel {
  products: MbbProduct[];
  isLoadingExistingProductDetails: boolean;
  selectedMbbSubscriptionId: string;
  selectedMbbStepIndex: number;
}

const initialState = {
  products: [],
  isLoadingExistingProductDetails: false,
  selectedMbbSubscriptionId: '',
  selectedMbbStepIndex: 0,
};

@State<CustomerProductsMbbStateModel>({
  name: 'customerProductsMbb',
  defaults: initialState,
})
@Injectable()
export class CustomerProductMbbState {
  private formBuilder = inject(FormBuilder);
  private simService = inject(SimService);
  private store = inject(Store);
  private stateHelper = inject(StateHelperService);
  private subscriptionService = inject(SubscriptionService);
  private notificationService = inject(NotificationService);

  formState$: Observable<FormStateModel> = inject(Store).select(state => state.formState);

  @Action(MbbProductAddAction)
  public mbbProductAdd(ctx: StateContext<CustomerProductsMbbStateModel>, { product }: MbbProductAddAction) {
    const state = ctx.getState();

    if (!product.id) {
      product.id = this.stateHelper.getIncrementedProductId(state.products, ProductStartIndex.MBB);
      product.isExistingSubscription = false;
    }

    ctx.setState(patch({ products: append([product]) }));

    ctx.dispatch(new SetSelectedMbbSubscriptionIdAction(product.id));
  }

  @Action(MbbProductSubscriberUpdateAction)
  public updateSubscriberAction(ctx: StateContext<CustomerProductsMbbStateModel>, action: MbbProductSubscriberUpdateAction) {
    const state = ctx.getState();
    const stateProduct = state.products.find(p => p.id === action.productId);
    ctx.setState(
      patch({
        products: updateItem<MbbProduct>(
          p => p.id === action.productId,
          patch({
            id: action.productId,
            subscriber: { ...stateProduct?.subscriber, ...action.subscriber },
          })
        ),
      })
    );
  }

  @Action(UpdateMbbDeliveryOptionAction)
  public updateMbbDeliveryOption(
    { setState, getState, dispatch }: StateContext<CustomerProductsMbbStateModel>,
    { productId, deliveryOption }: UpdateMbbDeliveryOptionAction
  ) {
    const mbbProduct = getState().products.find(p => p.id === productId);
    let updatedPaymentType = PaymentType.Billing;
    let mbbRouter = mbbProduct.mbbRouter;
    let selectedBundle = mbbProduct.selectedBundle;

    switch (deliveryOption) {
      case MbbDeliveryOptions.SendRouterInPost:
        updatedPaymentType = PaymentType.PayWithNextInvoice;
        if (mbbProduct.mbbRouter?.offeringId) {
          dispatch(
            new AddShippingItemAction({
              id: productId,
              name: mbbProduct.mbbRouter?.name,
              offeringId: mbbProduct.mbbRouter?.offeringId,
              productClassification: ProductClassifications.MBBProduct,
              icon: IconNames.ice_power,
              image: mbbProduct.mbbRouter?.image?.downloadUri,
            })
          );
        }
        break;
      case MbbDeliveryOptions.SellInStore:
        updatedPaymentType = PaymentType.Billing;
        dispatch(new RemoveShippingItemAction(productId, ProductClassifications.MBBProduct));
        break;
      case MbbDeliveryOptions.AlreadyOwnRouter:
        mbbRouter = new MbbRouter();
        selectedBundle = null;
        updatedPaymentType = PaymentType.Billing;
        dispatch(new RemoveShippingItemAction(productId, ProductClassifications.MBBProduct));
        break;
    }

    setState(
      patch({
        products: updateItem<MbbProduct>(
          item => item.id === productId,
          patch<MbbProduct>({
            deliveryOption: deliveryOption,
            paymentType: updatedPaymentType,
            mbbRouter,
            selectedBundle,
          })
        ),
      })
    );
  }

  @Action(MbbProductUpdateAction)
  public mbbProductUpdate({ setState }: StateContext<CustomerProductsMbbStateModel>, { product }: MbbProductUpdateAction) {
    if (!product?.mbbRouter) {
      return;
    }

    const selectedBundle = product.mbbRouter.bundles
      ? getCorrelatedBundle(product.mbbRouter, product.mbbSubscription?.id)
      : null;

    setState(
      patch({
        products: updateItem<MbbProduct>(
          (p: MbbProduct) => p.id == product.id,
          patch({
            id: product.id,
            routerName: product.routerName,
            imei: product.imei,
            icc: product.icc,
            orderSim: product.orderSim,
            selectedBundle: selectedBundle,
            mbbRouter: product.mbbRouter,
            mbbSubscription: product.mbbSubscription,
            selectedChangeInfo: product.selectedChangeInfo,
            activationDate: product.activationDate,
          })
        ),
      })
    );

    if (!product.mbbRouter?.offeringId) {
      return;
    }

    const updatedChild = new ShoppingCartChildItem();
    updatedChild.id = product.mbbRouter.offeringId;
    updatedChild.name = product.mbbRouter.name;
    updatedChild.icon = IconNames.ice_ruter;
    updatedChild.image = product.mbbRouter.image?.previewUri;

    switch (product.deliveryOption) {
      case MbbDeliveryOptions.SendRouterInPost:
        this.store.dispatch(
          new AddOrUpdateShippingItemAction({
            id: product.id,
            name: product.mbbRouter?.name,
            offeringId: product.mbbRouter.offeringId,
            productClassification: ProductClassifications.MBBProduct,
            icon: IconNames.ice_ruter,
            image: product.mbbRouter.image?.downloadUri,
          })
        );
        break;

      case MbbDeliveryOptions.SellInStore:
      case MbbDeliveryOptions.AlreadyOwnRouter:
        break;
    }
  }

  @Action(UpdateMbbRouterDetailsAction)
  public mbbProductUpdateRouter(
    { setState }: StateContext<CustomerProductsMbbStateModel>,
    { productId, icc, imei, routerName }: UpdateMbbRouterDetailsAction
  ) {
    if (!productId) {
      return;
    }

    setState(
      patch({
        products: updateItem<MbbProduct>(
          (p: MbbProduct) => p.id == productId,
          patch({ routerName: routerName, imei: imei, icc: icc })
        ),
      })
    );
  }

  @Action(MbbProductRemoveConfirmAction)
  public mbbProductRemoveConfirm(
    { setState, getState, dispatch }: StateContext<CustomerProductsMbbStateModel>,
    { productId }: MbbProductRemoveConfirmAction
  ) {
    const state = getState();
    if (state.products.find(x => x.id === productId)?.isExistingSubscription) {
      setState(
        patch({
          products: updateItem<MbbProduct>(
            p => p.id === productId,
            patch({ mbbSubscription: new MbbSubscriptionOffering(), selectedChangeInfo: null })
          ),
        })
      );
      dispatch([
        new RemoveShippingItemAction(productId, ProductClassifications.MBBProduct),
        new RemoveShippingItemAction(productId, ProductClassifications.DataSim),
      ]);
    } else {
      let formState: FormStateModel;
      let list: FormArray;

      const reducedCustomerProducts = state.products.filter(p => p.id !== productId);

      const subscription$ = this.formState$.subscribe((value: FormStateModel) => {
        formState = value;
        list = value.mbbForm.model.list.filter(p => p.productId !== productId) as any;
      });

      setState({ ...state, products: reducedCustomerProducts });

      const resetMbbForm = list.length === 0;

      dispatch([
        new RemoveShippingItemAction(productId, ProductClassifications.MBBProduct),
        new UpdateFormValue({ path: 'formState.mbbForm', value: { list } }),
        new UpdateMbbFormValidStatusAction(formState, this.formBuilder, this.simService, resetMbbForm),
      ]);

      subscription$.unsubscribe();
    }
  }

  @Action(MbbProductResetAction)
  public mbbProductReset(ctx: StateContext<CustomerProductsMbbStateModel>) {
    ctx.patchState(initialState);
  }

  @Action(SetExistingMbbSubscriptionsAction)
  public setVoiceSubscriptions(
    { patchState, dispatch }: StateContext<CustomerProductsMbbStateModel>,
    { mbbSubscriptions }: SetExistingMbbSubscriptionsAction
  ) {
    const existingMbbProducts = mbbSubscriptions.map((subscription: Subscription) => {
      const existingProduct = new MbbProduct();
      existingProduct.id = subscription.id;
      existingProduct.isExistingSubscription = true;
      existingProduct.existingSubscription = subscription;
      return existingProduct;
    });

    patchState({ isLoadingExistingProductDetails: true, products: existingMbbProducts });
    dispatch(new ApiGetAssignedMbbSubscriptionsAction(existingMbbProducts.map(x => x.id.toString())));
  }

  @Action(ApiGetAssignedMbbSubscriptionsAction)
  public getAssignedOfferingForSubscriptions(
    { dispatch }: StateContext<CustomerProductsMbbStateModel>,
    { subscriptionIds }: ApiGetAssignedMbbSubscriptionsAction
  ) {
    return this.subscriptionService.getAssignedSubscriptions(subscriptionIds).pipe(
      tap(
        response => dispatch(new ApiGetAssignedMbbSubscriptionsSuccessAction(response)),
        () => dispatch(new ApiGetAssignedMbbSubscriptionsErrorAction())
      )
    );
  }

  @Action(ApiGetAssignedMbbSubscriptionsSuccessAction)
  public getAssignedOfferingForSubscriptionsSuccess(
    { dispatch, patchState }: StateContext<CustomerProductsMbbStateModel>,
    { assignedSubscriptions }: ApiGetAssignedMbbSubscriptionsSuccessAction
  ) {
    assignedSubscriptions.forEach((assignedSubscription: AssignedSubscriptionModel) =>
      dispatch(new SetAssignedMbbSubscriptionAction(assignedSubscription))
    );
    patchState({ isLoadingExistingProductDetails: false });
  }

  @Action(SetAssignedMbbSubscriptionAction)
  public setAssignedOfferingOnSubscription(
    { setState }: StateContext<CustomerProductsMbbStateModel>,
    { assignedSubscription }: SetAssignedMbbSubscriptionAction
  ) {
    setState(
      patch<CustomerProductsMbbStateModel>({
        products: updateItem<MbbProduct>(
          item => item.id.toString() === assignedSubscription.subscriptionNo.toString(),
          patch<MbbProduct>({
            assignedSubscription: assignedSubscription,
          })
        ),
      })
    );
  }

  @Action(ApiGetAssignedMbbSubscriptionsErrorAction)
  public getAssignedOfferingForSubscriptionsError({ patchState }: StateContext<CustomerProductsMbbStateModel>) {
    this.notificationService.error(Messages.AssignedOfferingForSubscriptionsError);
    patchState({ isLoadingExistingProductDetails: false });
  }

  @Action(ApiGetMbbSubscriptionOfferingsAction)
  public getMbbSubscriptionOfferings(
    { dispatch, setState }: StateContext<CustomerProductsMbbStateModel>,
    { subscriptionId }: ApiGetMbbSubscriptionOfferingsAction
  ) {
    setState(
      patch({
        products: updateItem(
          x => x.existingSubscription.id === subscriptionId,
          patch<MbbProduct>({
            isLoadingSubscriptionOfferings: true,
          })
        ),
      })
    );
    return this.subscriptionService.getOfferingsForMbbSubscription(subscriptionId).pipe(
      tap(
        response => dispatch(new ApiGetMbbSubscriptionOfferingsSuccessAction(subscriptionId, response)),
        () =>
          dispatch(new ApiGetMbbSubscriptionOfferingsErrorAction(subscriptionId, Messages.GetMbbSubscriptionOfferingsError))
      )
    );
  }

  @Action(ApiGetMbbSubscriptionOfferingsSuccessAction)
  public getMbbSubscriptionOfferingsSuccess(
    { setState }: StateContext<CustomerProductsMbbStateModel>,
    { subscriptionId, offerings }: ApiGetMbbSubscriptionOfferingsSuccessAction
  ) {
    setState(
      patch({
        products: updateItem(
          x => x.existingSubscription.id === subscriptionId,
          patch<MbbProduct>({
            allowedSubscriptionOfferings: offerings,
            isLoadingSubscriptionOfferings: false,
          })
        ),
      })
    );
  }

  @Action(ApiGetMbbSubscriptionOfferingsErrorAction)
  public getSubscriptionOfferingsError(
    { setState }: StateContext<CustomerProductsMbbStateModel>,
    { subscriptionId, error }: ApiGetMbbSubscriptionOfferingsErrorAction
  ) {
    setState(
      patch({
        products: updateItem(
          x => x.existingSubscription.id === subscriptionId,
          patch<MbbProduct>({
            isLoadingSubscriptionOfferings: false,
          })
        ),
      })
    );
    this.notificationService.error(error);
  }

  @Action(ResetSelectedMbbSubscriptionAction)
  public resetAccordion({ patchState }: StateContext<CustomerProductsMbbStateModel>) {
    patchState({
      selectedMbbSubscriptionId: '',
      selectedMbbStepIndex: 0,
    });
  }

  @Action(ToggleSelectedMbbSubscriptionAction)
  public toggleAccordion(
    { getState, patchState }: StateContext<CustomerProductsMbbStateModel>,
    { id }: ToggleSelectedMbbSubscriptionAction
  ) {
    const state = getState();
    if (state.selectedMbbSubscriptionId === id) {
      patchState({ selectedMbbSubscriptionId: '' });
      this.store.dispatch(new SetSelectedMbbStepIndexAction(0));
    } else {
      patchState({ selectedMbbSubscriptionId: id });
    }
  }

  @Action(SetSelectedMbbSubscriptionIdAction)
  public setAccordion(
    { patchState }: StateContext<CustomerProductsMbbStateModel>,
    { id }: SetSelectedMbbSubscriptionIdAction
  ) {
    patchState({ selectedMbbSubscriptionId: id });
  }

  @Action(SetSelectedMbbStepIndexAction)
  public setSelectedStepIndex(
    { getState, patchState }: StateContext<CustomerProductsMbbStateModel>,
    { index }: SetSelectedMbbStepIndexAction
  ) {
    const state = getState();
    if (state.selectedMbbStepIndex !== index) {
      patchState({ selectedMbbStepIndex: index });
    }
  }

  @Action(SetPaymentTypeOnMBBAction)
  public SetPaymentTypeAction(
    { setState }: StateContext<CustomerProductsMbbStateModel>,
    { productId, paymentType }: SetPaymentTypeOnMBBAction
  ) {
    setState(
      patch<CustomerProductsMbbStateModel>({
        products: updateItem<MbbProduct>(
          product => product.id === productId,
          patch<MbbProduct>({ paymentType: paymentType })
        ),
      })
    );
  }
}
